'use client'

import { useMemo, type ReactNode } from 'react'
import { useRouterInfo } from 'shared'
import { AButton, APicture } from 'ui'
import { CraftAssetField, CraftLinkItField } from 'tsconfig/craft-types'
import './c-partial-text-masthead.css'
import { normalizeHref } from 'discovericl/helpers/normalize-href'
import classNames from 'classnames'

export type CPartialTextMastheadBlock = {
  id?: string
  typeHandle?: string
  idName?: string
  heading?: string
  subheading?: string
  button?: CraftLinkItField
  imageDesktop?: Array<CraftAssetField>
  imageMobile?: Array<CraftAssetField>
}

type Props = {
  field: Array<CPartialTextMastheadBlock>
}

export const CPartialTextMasthead = ({ field }: Props) => {
  const router = useRouterInfo()
  const isJapanLocale = router.locale === 'ja-JP'
  
  const content = useMemo(() => {
    return field && field.length ? field[0] : null
  }, [field])

  const formattedHeading: ReactNode = useMemo(() => {
    const heading = content?.heading
    if (!heading) return null

    if (isJapanLocale && heading.includes(' ')) {
      const spaceIndex = heading.indexOf(' ')
      const firstPart = heading.substring(0, spaceIndex)
      const secondPart = heading.substring(spaceIndex + 1).trim()

      if (secondPart) {
        return (
          <>
            {firstPart}
            <br />
            {secondPart}
          </>
        )
      }
    }

    return heading
  }, [content?.heading, isJapanLocale])

  return (
    <section className="c-partial-text-masthead">
      <div className="c-partial-text-masthead__spacer mb-6 lg:mb-16" />
      <div className="relative">
        <div className="o-container o-container--xl">
          <div className="c-partial-text-masthead__left">
            {formattedHeading && (
              <h1 className="lg:font-light text-h6 lg:text-h3 text-neutral-dark-gray">
                {formattedHeading}
              </h1>
            )}
            {content?.subheading && (
              <div className={classNames(
                'mt-4 text-primary-gray',
                {
                  'text-p': isJapanLocale,
                  'text-h7': !isJapanLocale,
                }
              )}>
                {content.subheading}
              </div>
            )}
            {content?.button && content?.button?.url && (
              <div className="mt-4">
                <AButton
                  type={content.button?.type === 'entry' ? 'link' : 'a'}
                  target={content.button?.target}
                  href={
                    content.button?.type === 'entry'
                      ? normalizeHref(content.button?.url || '')
                      : content.button?.url || ''
                  }
                  style="outline"
                  color="teal"
                >
                  <span>{content?.button?.text ?? ''}</span>
                </AButton>
              </div>
            )}
          </div>
          <div className="c-partial-text-masthead__right hidden md:block mt-10 xl:mt-0 -mx-6 xl:mx-0">
            <APicture
              className="block w-full h-full object-cover"
              imageDesktop={content?.imageDesktop && content.imageDesktop[0]}
              imageMobile={content?.imageMobile && content.imageMobile[0]}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

export { CPartialTextMastheadFragments } from '../../fragments/c-partial-text-masthead'
